<template>
  <article
    v-if="item"
    class="basket-product-card"
    :class="{
      'basket-product-card--unavailable': isUnavailable,
      'basket-product-card--gift': isGift,
    }"
    :data-testid="`basket-item-${item.id}`"
  >
    <!-- 🎁 Лента «Подарок» для подарочного блюда по промокоду -->
    <div v-if="isGift" class="basket-product-card__gift-ribbon">
      <span aria-hidden="true">🎁</span> Подарок
    </div>

    <!-- Верхняя часть: изображение и название -->
    <div class="basket-product-card__top">
      <!-- Изображение товара -->
      <BasketProductImage :images="item.img" :name="item.name" :placeholder="item.placeholderImg" />

      <!-- Название -->
      <div class="basket-product-card__name">
        {{ item.name }}
        <span v-if="sizeLabel" class="basket-product-card__size">{{ sizeLabel }}</span>
      </div>
      <!--
        Обычный товар: счётчик количества.
        Подарок в одну штуку: фиксированная метка без изменения кол-ва.
        Подарок, у которого в корзине несколько штук («усыновлённый» — гость сам положил
        это блюдо): счётчик остаётся, иначе платные единицы не видно и не убрать.
      -->
      <UiBaseCounter
        v-if="!isGift || hasPaidUnits"
        size="sm"
        :count-v="quantity"
        :step="quantityStep"
        :min-v="isGift ? 1 : minQuantity"
        class="basket-product-card__counter"
        @count="handleQuantityChange"
      />
      <span v-else class="basket-product-card__gift-qty">×1</span>
    </div>

    <!-- Добавки и исключения -->
    <div v-if="item.selectedAddons?.length > 0 || item.selectedExcludes?.length > 0" class="basket-product-card__details">
      <BasketProductAddons
        v-if="item.selectedAddons?.length > 0"
        :addons="item.selectedAddons.map(a => ({ name: a.name, price: String(a.price) }))"
        class="basket-product-card__addons"
      />

      <BasketProductExcludes
        v-if="item.selectedExcludes?.length > 0"
        :excludes="item.selectedExcludes"
        class="basket-product-card__excludes"
      />
    </div>

    <!-- ⚠️ Предупреждение о недоступности -->
    <div v-if="isUnavailable" class="basket-product-card__warning">
      <svg class="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
      </svg>
      <div>
        <p class="font-semibold">Товар недоступен</p>
        <p class="text-xs">Снят с продажи или закончился — удалите его из корзины, чтобы оформить заказ</p>
      </div>
    </div>

    <!-- Итоговая цена -->
    <div class="basket-product-card__price">
      <span>Итого</span>
      <!--
        Бесплатна ровно ОДНА единица подарка. Если в корзине их несколько, показываем
        цену оплачиваемых: иначе строка говорила «Бесплатно», а «Сумма заказа» молча
        включала остальные штуки — детализация не сходилась.
      -->
      <div v-if="isGift" class="basket-product-card__price-value basket-product-card__price-value--gift">
        <template v-if="hasPaidUnits">
          {{ paidTotalPrice }} ₽
          <span class="basket-product-card__gift-note">1 шт. в подарок</span>
        </template>
        <template v-else>
          <s v-if="giftOriginalPrice > 0" class="basket-product-card__price-old">{{ giftOriginalPrice }} ₽</s>
          Бесплатно
        </template>
      </div>
      <div v-else class="basket-product-card__price-value">{{ totalPrice }} ₽</div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import consola from 'consola';
import { useInstitutionStore } from '@entities/institution';
import { useCartStore } from '@entities/order';
import { productSizeLabel } from '@entities/product';
import { useCartProduct, extractIds, type EnrichedCartItem } from '@features/basket';

interface Props {
  item: EnrichedCartItem;
}

const props = defineProps<Props>();

if (!props.item) {
  consola.error('[BasketProductCard] Item is undefined!', props);
}

const institutionStore = useInstitutionStore();

const branchId = computed(() => {
  return props.item.branch_id || institutionStore.selectedBranch?.id;
});

const addonIds = computed(() => {
  return extractIds(props.item.addons || []);
});

const excludeIds = computed(() => {
  return extractIds(props.item.excludes || []);
});

const findProductInStore = (productId: string | number): any => {
  const id = Number(productId);

  if (!id || Number.isNaN(id)) {
    consola.warn('[BasketProductCard] Invalid productId:', productId);
    return null;
  }

  for (const category of institutionStore.products) {
    if (!category.products) continue;

    for (const product of category.products) {
      if (product.type === 'single' && Number(product.id) === id) {
        return product as any;
      }

      if (product.type === 'group' && product.variants) {
        const variant = product.variants.find((v: any) => Number(v.id) === id);
        if (variant) return variant as any;
      }
    }
  }

  consola.warn('[BasketProductCard] Product not found in store:', { productId: id });
  return null;
};

const productData = computed(() => {
  const rawProductId = props.item.productId || props.item.id;
  const productId = typeof rawProductId === 'string' ? Number(rawProductId) : rawProductId;

  if (!institutionStore.products || institutionStore.products.length === 0) {
    return null;
  }

  return findProductInStore(productId);
});

const quantityStep = computed(() => productData.value?.quantity_step ?? 1);
const minQuantity = computed(() => productData.value?.min_quantity ?? 0);

// Размер позиции рядом с названием: у позиции он бывает свой (пицца «50см», шаурма
// «Большая»), иначе собираем из каталога — там же, где его берёт состав комбо,
// чтобы одна и та же пицца подписывалась в корзине одинаково.
const sizeLabel = computed(
  () => props.item.size?.trim() || productSizeLabel(productData.value),
);

const { quantity, removeFromCart } = useCartProduct({
  product: computed(() => props.item),
  branchId,
  addons: addonIds as any,
  excludes: excludeIds as any,
});

const isUnavailable = computed(() => props.item?.stop_list === true);

const isGift = computed(() => props.item?.isGift === true);

// Исходная цена подарка (перечёркиваем её рядом с «Бесплатно»).
const giftOriginalPrice = computed(() => Math.round(props.item?.pricePerItem || 0));

// Бесплатна ровно одна единица подарка — остальные оплачиваются (так же считает касса).
const paidQuantity = computed(() =>
  isGift.value ? Math.max(0, quantity.value - 1) : quantity.value,
);
const hasPaidUnits = computed(() => isGift.value && paidQuantity.value > 0);
const paidTotalPrice = computed(() =>
  Math.round((props.item?.pricePerItem || 0) * paidQuantity.value),
);

const totalPrice = computed(() => {
  return Math.round(props.item.pricePerItem * quantity.value);
});

function handleQuantityChange(newQuantity: number): void {
  if (newQuantity <= 0) {
    removeFromCart();
    return;
  }

  const cartStore = useCartStore();
  const numericBranchId = branchId.value;
  if (!numericBranchId) return;

  cartStore.updateQuantity(
    props.item.id,
    newQuantity,
    numericBranchId,
    addonIds.value,
    excludeIds.value,
  );
}
</script>

<style scoped lang="scss">
.basket-product-card {
  // Тон как у каталожной карточки: нейтральный zinc + акцент, без синего.
  // Скругление — из пресета «Скругления элементов» (раздел «Внешний вид»),
  // чтобы карточки корзины и каталога не расходились по форме.
  @apply relative overflow-hidden;
  border-radius: var(--app-radius-card, 24px);
  background: linear-gradient(135deg, rgba(229, 231, 235, 1), rgba(229, 231, 235, 0.5));
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.05),
    0 10px 15px -3px rgba(0, 0, 0, 0.05);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0, 0, 0, 0.06);

  &:hover {
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.08),
      0 20px 25px -5px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
    border-color: rgba(239, 68, 68, 0.3);
  }

  &:active {
    transform: translateY(0) scale(0.995);
    transition-duration: 0.15s;
  }

  // На телефоне карточка мельче — скругление тоже чуть скромнее, но из того же пресета
  @media (max-width: 768px) {
    border-radius: calc(var(--app-radius-card, 24px) * 0.7);
  }

  &--unavailable {
    @apply opacity-75;
    border: 2px solid #ef4444 !important;
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.08), rgba(239, 68, 68, 0.04)) !important;

    &:hover {
      transform: none;
      border-color: #dc2626 !important;
    }
  }

  // 🎁 Подарочная карточка — зелёный акцент, чтобы пользователь сразу видел подарок
  &--gift {
    border: 2px solid #10b981 !important;
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.10), rgba(16, 185, 129, 0.03)) !important;

    &:hover {
      border-color: #059669 !important;
    }
  }

  &__gift-ribbon {
    @apply flex items-center gap-1 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white;
    background: linear-gradient(135deg, #10b981, #059669);
    letter-spacing: 0.05em;
  }

  &__gift-qty {
    @apply flex-shrink-0 text-sm font-bold text-emerald-600 dark:text-emerald-400;
  }

  &__price-value--gift {
    @apply flex items-baseline gap-2 text-emerald-600 dark:text-emerald-400;
  }

  &__gift-note {
    @apply text-xs font-medium text-emerald-600 dark:text-emerald-400;
  }

  &__price-old {
    @apply text-sm font-medium text-gray-400 dark:text-gray-500 line-through;
  }

  &__warning {
    @apply flex items-start gap-3 px-5 py-4;
    @apply bg-red-50 dark:bg-red-900/20;
    @apply border-t-2 border-b-2 border-red-200 dark:border-red-800;
    @apply text-red-800 dark:text-red-200;
  }

  &__top {
    @apply flex items-center px-4 py-4 gap-2;
  }

  :deep(.basket-product-image) {
    @apply flex-shrink-0;
    width: 56px;
    height: 56px;

    @media (max-width: 768px) {
      width: 48px;
      height: 48px;
    }
  }

  &__name {
    @apply text-sm font-semibold text-gray-900 dark:text-white tracking-tight leading-tight flex-1 min-w-0;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    line-height: 1.3;
  }

  &__size {
    @apply text-xs font-normal text-gray-500 dark:text-gray-400;
  }

  &__counter {
    @apply flex-shrink-0;
    min-width: 90px;
    max-width: 110px;
  }

  &__details {
    @apply px-5 py-3 space-y-2;
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.02), rgba(0, 0, 0, 0.01));
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  &__price {
    @apply flex gap-3 justify-between items-center px-5 pb-5 pt-4;
    border-top: 1px solid rgba(0, 0, 0, 0.06);

    span {
      @apply text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider;
    }
  }

  &__price-value {
    @apply text-xl font-bold text-gray-900 dark:text-white tracking-tight;
    font-variant-numeric: tabular-nums;
  }
}

// Тёмная тема — нейтральный zinc как у .dish-card на главной (без синего отлива cool-gray)
.dark .basket-product-card {
  background: linear-gradient(135deg, rgba(24, 24, 27, 1), rgba(24, 24, 27, 0.5));
  border-color: rgba(255, 255, 255, 0.1);

  &:hover {
    border-color: rgba(239, 68, 68, 0.4);
  }

  .basket-product-card__details {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.01));
    border-top-color: rgba(255, 255, 255, 0.08);
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }

  .basket-product-card__price {
    border-top-color: rgba(255, 255, 255, 0.08);
  }
}

@media (hover: none) and (pointer: coarse) {
  .basket-product-card:hover {
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .basket-product-card {
    transition: none;
  }
}
</style>
